import React, { FC, useState } from 'react'
import { Field, InjectedFormProps, reduxForm } from 'redux-form'
import { Form, Collapse, Row, Col, Image, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { debounce, isEmpty } from 'lodash'
import { useSelector } from 'react-redux'

// reducers
import { RootState } from '../../../reducers'

// atoms
import InputField from '../../../atoms/InputField'

// interfaces
import { IFormItemData } from '../../../types/interfaces'

// utils
import { ELEMENT_ATTRIBUTES, ELEMENT_TYPES, FORM, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT } from '../../../utils/enums'

// assets
import { ReactComponent as ChevronIcon } from '../../../assets/icons/BaseIcons/chevron-down-1-icon.svg'
import { ReactComponent as HeightIcon } from '../../../assets/icons/BaseMiniIcons/height-icon.svg'
import { ReactComponent as WidthIcon } from '../../../assets/icons/BaseMiniIcons/width-icon.svg'
import { ReactComponent as NoImageIcon } from '../../../assets/icons/ElementIcons/image-icon.svg'
import { ReactComponent as TrashIcon } from '../../../assets/icons/BaseIcons/trash-1-icon.svg'
import SelectField from '../../../atoms/SelectField'
import ValidationModal from '../../modals/ValidationModal'

const { Panel } = Collapse

export type ComponentProps = {
	onRemoveItem: Function
}
export interface Item {
	id: number
	value: string
	label: string
}

export interface ContainerState {
	cards: Item[]
}

type Props = InjectedFormProps<IFormItemData, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
	}

const checkURL = (url: any) => {
	if (url) {
		return url.match(/\.(jpeg|jpg|gif|png)$/) != null
	}
	return false
}

// validate required fields
const validateRequired = (value: string) => {
	if (checkURL(value) || isEmpty(value)) {
		return undefined
	}
	return 'This is not valid image url!'
}

const ImageElementForm: FC<Props> = (props) => {
	const { t } = useTranslation()
	const { handleSubmit, onRemoveItem } = props
	const [isValidationModalShowed, setValidationModalShowed] = useState(false)
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const formValues = useSelector((state: RootState) => state.form?.[FORM.IMAGE_ELEMENT_FORM]?.values)
	const columns = useSelector((state: RootState) => state.formTemplate.columns)

	return (
		<>
			<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
				<Row className={'mx-4 mb-4'} justify={'space-between'}>
					<h3>{t('loc:Image')}</h3>
					<TrashIcon className={'trashIcon'} onClick={() => onRemoveItem(formValues?.i)} />
				</Row>
				<Row justify={'center'} className={'my-2 w-full border-none px-4'}>
					<Col span={24}>
						<Field
							component={InputField}
							placeholder={t('loc:Enter link to image')}
							name={ELEMENT_ATTRIBUTES.IMAGE}
							size={'medium'}
							disabled={disable}
							allowClear={true}
							validate={validateRequired}
						/>
					</Col>
					<Col span={24}>
						<Field
							component={SelectField}
							label={t('loc:Column in table')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={ELEMENT_ATTRIBUTES.TABLE_COLUMN}
							options={columns?.data}
							size={'medium'}
							disabled={disable}
						/>
					</Col>
				</Row>
				{checkURL(formValues?.[ELEMENT_ATTRIBUTES.IMAGE]) ? (
					<Row justify={'center'} className={'my-2 w-full border-none px-4'}>
						<Col span={24} className={'imageError'}>
							<Image className={'w-full h-44 rounded-sm'} src={formValues?.[ELEMENT_ATTRIBUTES.IMAGE]} fallback={`${NoImageIcon}`} />
						</Col>
					</Row>
				) : null}
				<Collapse expandIcon={({ isActive }) => <ChevronIcon rotate={isActive ? 0 : 180} />} expandIconPosition={'end'} ghost>
					<Panel header={t('loc:Size')} key='1'>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11}>
								<Field
									className={'element-width'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.COL}
									size={'medium'}
									suffix={<WidthIcon />}
									disabled={disable}
									maxLength={2}
								/>
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									className={'element-height'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.ROW}
									size={'medium'}
									suffix={<HeightIcon />}
									disabled={disable}
									maxLength={3}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Validations')} key='2'>
						<Button
							className={'unfilled-btn w-full h-25px mb-2'}
							disabled={disable}
							onClick={() => {
								setValidationModalShowed(true)
							}}
						>
							{t('loc:Validation options')}
						</Button>
					</Panel>
				</Collapse>
			</Form>
			<ValidationModal
				width={460}
				typeOfElementForm={FORM.IMAGE_ELEMENT_FORM}
				isVisible={isValidationModalShowed}
				onCancel={() => {
					setValidationModalShowed(false)
				}}
				elementLabel={formValues?.label}
				elementType={ELEMENT_TYPES.IMAGE}
			/>
		</>
	)
}

const form: any = reduxForm<IFormItemData, ComponentProps>({
	form: FORM.IMAGE_ELEMENT_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	onChange: debounce((values, dispatch, props) => {
		if (props.anyTouched) {
			props.submit()
		}
	}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)
})(ImageElementForm)

export default form
